import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import defaultTo from 'lodash/defaultTo';
import get from 'lodash/get';
import { ValueType } from '@atlaskit/select';
import { N300 } from '@atlaskit/theme/colors';
import Button from '@atlaskit/button/new';
import styled from '@emotion/styled';
import {
  InvoiceGroup,
  PaymentMethodType,
  PaymentMethodTypeV2,
  ContactInfo,
  EndCustomerContact,
} from '@css-xp/graphtypes/src/generated-types-new';
import {
  COMMERCIAL_PRICE_ELIGIBILITY,
  ELIGIBLE_BILLING_PROFILES_FOR_AMENDMENT,
  STATSIG_FEATURE_GATES,
} from '@css-xp/ppc-constants';
import { getCompanyNameRoute } from '@css-xp/ppc-utils';
import type { BillingProfileOption } from './order-summary';
import { BillingProfilePanel } from './billing-profile-panel';
import { SearchBillingProfile } from './search-billing-profile';
import { NoBillingProfile } from './no-billing-profile';
import { useStore } from '../../../../store';
import { useFeatureToggle } from '@css-xp/base';

interface Props {
  billingProfilesInfo: InvoiceGroup[] | null | undefined;
  selectedBillingProfile: ValueType<BillingProfileOption> | null;
  setSelectedBillingProfile: (_selectedBillingProfile: ValueType<BillingProfileOption> | null) => void;
  isDeferredPaymentMethodDisabled?: boolean;
  isNoPaymentMethodDisabled?: boolean;
  areNonCommercialBillingProfilesDisabled?: boolean;
  shouldAllowBillingProfileTransferBasedOnPricingEligibility?: boolean;
  allowedPricingEligibilityForBillingProfileTransfer?: string[];
  showPricingEligibility?: boolean;
  showPrimaryContacts?: boolean;
  primaryContacts?: ContactInfo[];
  isPaypalEnabled?: boolean;
  secondaryCustomerContacts?: ContactInfo[];
  atlassianAccountIds?: string[];
  setAtlassianAccountIds?: React.Dispatch<React.SetStateAction<string[]>>;
  atlassianAccountEmails?: string[];
  setAtlassianAccountEmails?: React.Dispatch<React.SetStateAction<string[]>>;
  openCreateBillingProfile?: () => void;
  currentCurrency?: string | null;
  disableByCurrency?: boolean;
  shouldAllowAddingContacts?: boolean;
  isNewBPCreationFlowEnabled?: boolean;
  customerDetailsRoute?: string;
}

const StyledBillingProfilesContainer = styled.div({
  gap: '16px',
  display: 'flex',
  flexDirection: 'column',
});

const StyledBillingProfilesHeader = styled.label({
  fontSize: '11px',
  fontWeight: 600,
  lineHeight: '12px',
  color: N300,
});

const StyledBillingProfilePanel = styled.div({
  overflow: 'scroll',
  overflowX: 'hidden',
  height: '405px',
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
});

export const getDisableReason = (
  isDisabledBasedOnPricingEligibilityAllowList: boolean,
  isDisabledBasedOnPaymentMethod: boolean,
  isDisabledBasedOnCurrency: boolean
) => {
  if (isDisabledBasedOnPaymentMethod && isDisabledBasedOnPricingEligibilityAllowList) {
    return 'This billing profile is setup with different pricing eligibility and payment method that is not suitable for this transaction';
  }
  if (isDisabledBasedOnPricingEligibilityAllowList && isDisabledBasedOnCurrency) {
    return 'This billing profile is setup with different pricing eligibility and currency';
  }
  if (isDisabledBasedOnPaymentMethod && isDisabledBasedOnCurrency) {
    return 'This billing profile is setup with a payment method and currency that is not suitable for this transaction';
  }
  if (isDisabledBasedOnPricingEligibilityAllowList) {
    return 'This billing profile is setup with different pricing eligibility';
  }
  if (isDisabledBasedOnPaymentMethod) {
    return 'This billing profile is setup with a payment method that is not suitable for this transaction';
  }
  if (isDisabledBasedOnCurrency) {
    return 'This billing profile is setup with a different currency';
  }
  return '';
};

export const getIsEnabledBasedOnThePaymentMethod = ({
  billingProfile,
  isDeferredPaymentMethodDisabled,
  isNoPaymentMethodDisabled,
  isPaypalEnabled,
}: {
  billingProfile: InvoiceGroup;
  isDeferredPaymentMethodDisabled: boolean;
  isNoPaymentMethodDisabled: boolean;
  isPaypalEnabled: boolean;
}) => {
  let isEnabledBasedOnPaymentMethod = true;

  if (isDeferredPaymentMethodDisabled && isNoPaymentMethodDisabled) {
    isEnabledBasedOnPaymentMethod = !!(
      billingProfile?.defaultPaymentMethod?.type === PaymentMethodType.CARD ||
      //@ts-ignore
      (isPaypalEnabled && billingProfile?.defaultPaymentMethod?.type === PaymentMethodTypeV2.PAYPAL)
    );
  } else if (isNoPaymentMethodDisabled) {
    isEnabledBasedOnPaymentMethod = !!billingProfile?.defaultPaymentMethod?.type;
  } else if (isDeferredPaymentMethodDisabled) {
    isEnabledBasedOnPaymentMethod = isPaypalEnabled
      ? billingProfile?.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED
      : billingProfile?.defaultPaymentMethod?.type !== PaymentMethodType.DEFERRED &&
        //@ts-ignore
        billingProfile?.defaultPaymentMethod?.type !== PaymentMethodTypeV2.PAYPAL;
  }
  return isEnabledBasedOnPaymentMethod;
};

export const ChangeBillingProfile = ({
  billingProfilesInfo,
  selectedBillingProfile,
  setSelectedBillingProfile,
  isDeferredPaymentMethodDisabled = false,
  isNoPaymentMethodDisabled = false,
  areNonCommercialBillingProfilesDisabled = false,
  shouldAllowBillingProfileTransferBasedOnPricingEligibility = false,
  allowedPricingEligibilityForBillingProfileTransfer = [],
  showPricingEligibility = false,
  showPrimaryContacts = false,
  primaryContacts,
  isPaypalEnabled = false,
  atlassianAccountIds,
  atlassianAccountEmails,
  setAtlassianAccountEmails,
  setAtlassianAccountIds,
  secondaryCustomerContacts,
  openCreateBillingProfile,
  currentCurrency,
  disableByCurrency,
  shouldAllowAddingContacts = true,
  isNewBPCreationFlowEnabled = false,
  customerDetailsRoute,
}: Props) => {
  const [isEnabled] = useFeatureToggle();

  // To filter active billing profiles and remove deactivated billing profiles from the list
  billingProfilesInfo = billingProfilesInfo?.filter((billingProfile) => billingProfile?.isActive === true);

  const [searchedBillingProfileInfo, setSearchedBillingProfileInfo] = useState<InvoiceGroup[] | null | undefined>(
    billingProfilesInfo
  );
  const [{ selectedPartnerAccountData, selectedCustomer }] = useStore();
  const transactionAccountId = selectedPartnerAccountData?.transactionAccountId;
  const isExposeAndSearchByBillingProfileId = isEnabled(
    STATSIG_FEATURE_GATES.ENABLE_EXPOSE_AND_SEARCH_BY_BILLING_PROFILE_ID
  );
  const isMultiCurrencyChangesEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_MULTI_CURRENCY_CHANGES);
  const isAchPaymentMethodFFEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);

  const onQueryChange = (searchQuery: string) => {
    if (searchQuery !== '') {
      const matchedBillingProfileInfo = billingProfilesInfo?.filter((billingProfile) => {
        const shipToPartyData =
          billingProfile.name +
          ' ' +
          billingProfile.shipToParty?.name +
          ' ' +
          billingProfile.shipToParty?.addressLine1 +
          ' ' +
          billingProfile.shipToParty?.addressLine2 +
          ' ' +
          billingProfile.shipToParty?.city +
          ' ' +
          billingProfile.shipToParty?.state +
          ' ' +
          billingProfile.shipToParty?.country +
          ' ' +
          billingProfile.shipToParty?.postcode;
        return isExposeAndSearchByBillingProfileId
          ? shipToPartyData.toLowerCase()?.includes(searchQuery.toLowerCase()) ||
              billingProfile.id?.includes(searchQuery.toLowerCase())
          : shipToPartyData.toLowerCase()?.includes(searchQuery.toLowerCase());
      });
      setSearchedBillingProfileInfo(matchedBillingProfileInfo);
    } else {
      setSearchedBillingProfileInfo(billingProfilesInfo);
    }
  };

  const clearSearchQuery = () => {
    setSearchedBillingProfileInfo(billingProfilesInfo);
  };

  const customerBaseRoute =
    customerDetailsRoute ??
    getCompanyNameRoute({
      txaId: transactionAccountId || '',
      companyName: selectedCustomer?.name || '',
    });

  const LinkToCustomerBillingProfile = isNewBPCreationFlowEnabled ? (
    <Button onClick={openCreateBillingProfile}>Add billing profile</Button>
  ) : (
    <Link
      to={`${customerBaseRoute}#billing-profiles`}
      data-testId="billing-profile-link"
      target="_blank"
      onClick={(e) => e.stopPropagation()}
    >
      Add billing profile
    </Link>
  );

  // To Be Removed
  const getIsEnabledBasedOnPricingEligibility = (billingProfile: InvoiceGroup) => {
    return billingProfile?.shipToPriceEligibility
      ? ELIGIBLE_BILLING_PROFILES_FOR_AMENDMENT.includes(billingProfile?.shipToPriceEligibility?.toLowerCase())
      : true;
  };

  const getIsEnabledBasedOnPricingEligibilityAllowList = (billingProfile: InvoiceGroup) => {
    if (shouldAllowBillingProfileTransferBasedOnPricingEligibility) {
      return allowedPricingEligibilityForBillingProfileTransfer
        .map((pricingEligibility: string) => pricingEligibility.toLowerCase())
        .includes(defaultTo(get(billingProfile, 'shipToPriceEligibility'), COMMERCIAL_PRICE_ELIGIBILITY).toLowerCase());
    }
    return true;
  };

  return (
    <>
      <StyledBillingProfilesContainer>
        <StyledBillingProfilesHeader>Billing profiles</StyledBillingProfilesHeader>
        <SearchBillingProfile
          onQueryChange={onQueryChange}
          clearSearchQuery={clearSearchQuery}
          helperMessage={'Enter billing profile name or sold-to address'}
          isDisabled={!billingProfilesInfo || billingProfilesInfo?.length === 0}
          placeholder="Search"
        />
        <StyledBillingProfilePanel>
          {searchedBillingProfileInfo && searchedBillingProfileInfo.length > 0 ? (
            searchedBillingProfileInfo?.map((billingProfile) => {
              // in any condition if default payment method type is not available,
              // do not disable the selection
              const isEnabledBasedOnPaymentMethod = getIsEnabledBasedOnThePaymentMethod({
                billingProfile,
                isDeferredPaymentMethodDisabled,
                isNoPaymentMethodDisabled,
                isPaypalEnabled,
              });

              const isEnabledBasedOnPricingEligibility = areNonCommercialBillingProfilesDisabled
                ? getIsEnabledBasedOnPricingEligibility(billingProfile)
                : true;
              const isEnabledBasedOnPricingEligibilityAllowList = getIsEnabledBasedOnPricingEligibilityAllowList(
                billingProfile
              );
              const isSameAsSelectedBillingProfileCurrency =
                disableByCurrency && currentCurrency ? currentCurrency === billingProfile.currency : true;
              const isEnabled =
                isEnabledBasedOnPaymentMethod &&
                isEnabledBasedOnPricingEligibility &&
                isEnabledBasedOnPricingEligibilityAllowList &&
                isSameAsSelectedBillingProfileCurrency;
              const primaryCustomerContact = primaryContacts?.find(
                (contact) => contact?.identifierId === billingProfile?.id
              );
              const secondaryContacts = secondaryCustomerContacts?.filter(
                (contact: ContactInfo) => contact?.identifierId === billingProfile?.id && !contact?.isPrimary
              );
              return (
                <BillingProfilePanel
                  key={`billing-profile-${billingProfile.id}`}
                  billingProfile={billingProfile}
                  setSelectedBillingProfile={setSelectedBillingProfile}
                  selectedBillingProfile={selectedBillingProfile}
                  isDisabled={!isEnabled}
                  showBillingProfileId={isExposeAndSearchByBillingProfileId}
                  showPricingEligibility={showPricingEligibility}
                  disableReason={getDisableReason(
                    !isEnabledBasedOnPricingEligibilityAllowList,
                    !isEnabledBasedOnPaymentMethod,
                    !isSameAsSelectedBillingProfileCurrency
                  )}
                  showPrimaryContacts={showPrimaryContacts}
                  primaryCustomerContact={primaryCustomerContact}
                  isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
                  atlassianAccountIds={atlassianAccountIds as string[]}
                  atlassianAccountEmails={atlassianAccountEmails as string[]}
                  setAtlassianAccountEmails={setAtlassianAccountEmails as any}
                  setAtlassianAccountIds={setAtlassianAccountIds as any}
                  secondaryCustomerContacts={secondaryContacts as EndCustomerContact[]}
                  allPrimaryContacts={primaryContacts as any}
                  shouldAllowAddingContacts={shouldAllowAddingContacts}
                  shouldShowACHPaymentMethod={isAchPaymentMethodFFEnabled}
                />
              );
            })
          ) : (
            <NoBillingProfile
              LinkToCustomerBillingProfile={LinkToCustomerBillingProfile}
              isAddBPDisabled={billingProfilesInfo ? billingProfilesInfo?.length > 0 : false}
            />
          )}
        </StyledBillingProfilePanel>
      </StyledBillingProfilesContainer>
    </>
  );
};
